import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../Header';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { toast } from 'sonner';
import {
  Laptop, Plus, Download, Loader2, RefreshCw, Copy, Check, Trash2, KeyRound,
  ChevronRight, ShieldCheck, MonitorSmartphone, Ticket,
} from 'lucide-react';
import { PostureBar, StatePill, DEVICE_STATUS_META, CODE_STATUS_META, relativeTime } from './deviceUi';

const API = '/api';

const StatCard = ({ label, value, icon: Icon, tone, testId }) => (
  <Card className="border-black/10 dark:border-white/10" data-testid={testId}>
    <CardContent className="p-4 flex items-center gap-3">
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${tone}`}>
        <Icon className="w-4.5 h-4.5" />
      </div>
      <div>
        <div className="text-2xl font-semibold text-[#0A0A0A] dark:text-[#F5F5F5] leading-none">{value}</div>
        <div className="text-[11px] uppercase tracking-wide text-black/45 dark:text-white/45 mt-1">{label}</div>
      </div>
    </CardContent>
  </Card>
);

export default function DevicesPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [devices, setDevices] = useState([]);
  const [stats, setStats] = useState({ total: 0, active: 0, attention: 0, retired: 0 });
  const [codes, setCodes] = useState([]);

  const [enrollOpen, setEnrollOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ label: '', platform: 'macos', max_uses: 5, ttl_days: 7 });
  const [newCode, setNewCode] = useState(null);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    try {
      const [dRes, cRes] = await Promise.all([
        axios.get(`${API}/devices`),
        axios.get(`${API}/devices/enrollment-codes`),
      ]);
      setDevices(dRes.data.devices || []);
      setStats(dRes.data.stats || {});
      setCodes(cRes.data.codes || []);
    } catch (e) {
      toast.error('Could not load devices');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const createCode = async () => {
    setCreating(true);
    try {
      const res = await axios.post(`${API}/devices/enrollment-codes`, {
        label: form.label || 'Device enrollment',
        platform: form.platform,
        max_uses: Number(form.max_uses) || 5,
        ttl_days: Number(form.ttl_days) || 7,
      });
      setNewCode(res.data.code);
      toast.success('Enrollment code created');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create code');
    } finally {
      setCreating(false);
    }
  };

  const revokeCode = async (id) => {
    try {
      await axios.delete(`${API}/devices/enrollment-codes/${id}`);
      toast.success('Code revoked');
      load();
    } catch (e) {
      toast.error('Failed to revoke code');
    }
  };

  const copyCode = async () => {
    if (!newCode) return;
    try {
      await navigator.clipboard.writeText(newCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error('Copy failed — please select and copy the code manually');
    }
  };

  const closeEnroll = () => {
    setEnrollOpen(false);
    setNewCode(null);
    setForm({ label: '', platform: 'macos', max_uses: 5, ttl_days: 7 });
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] dark:bg-[#050505]">
      <Header />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8" data-testid="devices-page">
        {/* Title */}
        <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
          <div>
            <h1 className="text-2xl font-semibold text-[#0A0A0A] dark:text-[#F5F5F5] flex items-center gap-2">
              <MonitorSmartphone className="w-6 h-6 text-[#0F62FE] dark:text-[#5C97FF]" /> Devices
            </h1>
            <p className="text-sm text-black/55 dark:text-white/55 mt-1">
              Enrolled endpoints reporting posture into Nova. Manage enrollment and review device evidence.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" data-testid="download-client-btn"
              onClick={() => navigate('/devices/download')}
              className="border-black/15 dark:border-white/15">
              <Download className="w-4 h-4 mr-1.5" /> Download Client
            </Button>
            <Button data-testid="enroll-device-btn" onClick={() => setEnrollOpen(true)}
              className="bg-[#0F62FE] hover:bg-[#0A4FCC] text-white">
              <Plus className="w-4 h-4 mr-1.5" /> Enroll Device
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          <StatCard label="Total devices" value={stats.total || 0} icon={Laptop} tone="bg-[#0F62FE]/10 text-[#0F62FE] dark:text-[#5C97FF]" testId="stat-total" />
          <StatCard label="Active" value={stats.active || 0} icon={ShieldCheck} tone="bg-emerald-500/10 text-emerald-600 dark:text-emerald-400" testId="stat-active" />
          <StatCard label="Needs attention" value={stats.attention || 0} icon={RefreshCw} tone="bg-red-500/10 text-red-600 dark:text-red-400" testId="stat-attention" />
          <StatCard label="Active codes" value={codes.filter((c) => c.status === 'active').length} icon={Ticket} tone="bg-amber-500/10 text-amber-600 dark:text-amber-400" testId="stat-codes" />
        </div>

        {/* Enrollment codes */}
        <Card className="border-black/10 dark:border-white/10 mb-6">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold text-[#0A0A0A] dark:text-[#F5F5F5] flex items-center gap-2">
                <KeyRound className="w-4 h-4 text-black/50 dark:text-white/50" /> Enrollment codes
              </h2>
              <Button size="sm" variant="ghost" onClick={() => setEnrollOpen(true)} data-testid="new-code-btn"
                className="text-[#0F62FE] dark:text-[#5C97FF] hover:bg-[#0F62FE]/5">
                <Plus className="w-3.5 h-3.5 mr-1" /> New code
              </Button>
            </div>
            {codes.length === 0 ? (
              <p className="text-[13px] text-black/45 dark:text-white/45 py-2">
                No enrollment codes yet. Create one and share it with the person setting up the device.
              </p>
            ) : (
              <div className="divide-y divide-black/5 dark:divide-white/5" data-testid="codes-list">
                {codes.map((c) => (
                  <div key={c.id} className="flex items-center gap-3 py-2.5" data-testid={`code-row-${c.display_id}`}>
                    <span className="font-mono text-[12px] text-black/70 dark:text-white/70 w-24 shrink-0">{c.display_id}</span>
                    <span className="text-[13px] text-[#0A0A0A] dark:text-[#F5F5F5] flex-1 truncate">{c.label}</span>
                    <StatePill meta={CODE_STATUS_META[c.status] || CODE_STATUS_META.expired} testId={`code-status-${c.display_id}`} />
                    <span className="text-[11px] text-black/45 dark:text-white/45 w-16 text-right">{c.uses}/{c.max_uses} uses</span>
                    {c.status === 'active' && (
                      <button onClick={() => revokeCode(c.id)} data-testid={`revoke-code-${c.display_id}`}
                        className="text-black/35 hover:text-red-600 dark:text-white/35 dark:hover:text-red-400 transition-colors" title="Revoke code">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Devices table */}
        <Card className="border-black/10 dark:border-white/10">
          <CardContent className="p-0">
            {loading ? (
              <div className="p-10 flex items-center justify-center text-black/40 dark:text-white/40">
                <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading devices…
              </div>
            ) : devices.length === 0 ? (
              <div className="p-10 text-center" data-testid="devices-empty">
                <Laptop className="w-10 h-10 mx-auto text-black/15 dark:text-white/15 mb-3" />
                <p className="text-sm text-black/55 dark:text-white/55">No devices enrolled yet.</p>
                <Button className="mt-4 bg-[#0F62FE] hover:bg-[#0A4FCC] text-white" onClick={() => setEnrollOpen(true)}>
                  <Plus className="w-4 h-4 mr-1.5" /> Enroll your first device
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto" data-testid="devices-table">
                <table className="w-full text-left">
                  <thead>
                    <tr className="text-[10px] uppercase tracking-wide text-black/40 dark:text-white/40 border-b border-black/8 dark:border-white/8">
                      <th className="font-medium px-4 py-2.5">Device</th>
                      <th className="font-medium px-4 py-2.5">Status</th>
                      <th className="font-medium px-4 py-2.5">Last check-in</th>
                      <th className="font-medium px-4 py-2.5">Posture</th>
                      <th className="font-medium px-4 py-2.5"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {devices.map((d) => (
                      <tr key={d.id} onClick={() => navigate(`/devices/${d.id}`)} data-testid={`device-row-${d.id}`}
                        className="border-b border-black/5 dark:border-white/5 hover:bg-black/[0.015] dark:hover:bg-white/[0.02] cursor-pointer transition-colors">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2.5">
                            <Laptop className="w-4 h-4 text-black/40 dark:text-white/40 shrink-0" />
                            <div className="min-w-0">
                              <div className="text-[13px] font-medium text-[#0A0A0A] dark:text-[#F5F5F5] truncate">{d.hostname || 'Unknown device'}</div>
                              <div className="text-[11px] text-black/45 dark:text-white/45">{d.model || d.platform} · macOS {d.os_version || '—'}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <StatePill meta={DEVICE_STATUS_META[d.status] || DEVICE_STATUS_META.inactive} testId={`device-status-${d.id}`} />
                        </td>
                        <td className="px-4 py-3 text-[12px] text-black/55 dark:text-white/55">{relativeTime(d.last_seen_at)}</td>
                        <td className="px-4 py-3"><PostureBar summary={(d.latest_posture || {}).summary} /></td>
                        <td className="px-4 py-3 text-right"><ChevronRight className="w-4 h-4 text-black/25 dark:text-white/25 inline" /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Enroll dialog */}
      <Dialog open={enrollOpen} onOpenChange={(o) => (o ? setEnrollOpen(true) : closeEnroll())}>
        <DialogContent className="sm:max-w-md" data-testid="enroll-dialog">
          {!newCode ? (
            <>
              <DialogHeader>
                <DialogTitle>Enroll a device</DialogTitle>
                <DialogDescription>
                  Create an enrollment code, then share it with the person setting up the device.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3 py-1">
                <div>
                  <Label className="text-[12px]">Label</Label>
                  <Input data-testid="code-label-input" placeholder="e.g. Neha's MacBook"
                    value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} className="mt-1" />
                </div>
                <div>
                  <Label className="text-[12px]">Platform</Label>
                  <Select value={form.platform} onValueChange={(v) => setForm({ ...form, platform: v })}>
                    <SelectTrigger className="mt-1" data-testid="code-platform-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="macos">macOS</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[12px]">Max uses</Label>
                    <Input data-testid="code-maxuses-input" type="number" min={1} value={form.max_uses}
                      onChange={(e) => setForm({ ...form, max_uses: e.target.value })} className="mt-1" />
                  </div>
                  <div>
                    <Label className="text-[12px]">Expires in (days)</Label>
                    <Input data-testid="code-ttl-input" type="number" min={1} value={form.ttl_days}
                      onChange={(e) => setForm({ ...form, ttl_days: e.target.value })} className="mt-1" />
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={closeEnroll}>Cancel</Button>
                <Button data-testid="create-code-btn" onClick={createCode} disabled={creating}
                  className="bg-[#0F62FE] hover:bg-[#0A4FCC] text-white">
                  {creating ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <KeyRound className="w-4 h-4 mr-1.5" />}
                  Create code
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>Enrollment code ready</DialogTitle>
                <DialogDescription>
                  This code is shown only once. Copy it now and share it securely.
                </DialogDescription>
              </DialogHeader>
              <div className="py-2">
                <div className="flex items-center gap-2 rounded-lg border border-[#0F62FE]/30 bg-[#0F62FE]/5 dark:bg-[#0F62FE]/10 p-3">
                  <code data-testid="new-code-value" className="font-mono text-lg font-semibold text-[#0A0A0A] dark:text-[#F5F5F5] flex-1 tracking-wide">{newCode}</code>
                  <Button size="sm" variant="ghost" onClick={copyCode} data-testid="copy-code-btn" className="text-[#0F62FE] dark:text-[#5C97FF]">
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
                <ol className="mt-4 space-y-1.5 text-[13px] text-black/60 dark:text-white/60 list-decimal list-inside">
                  <li>Open the Nova Device Client on the device.</li>
                  <li>Paste this enrollment code.</li>
                  <li>Click <span className="font-medium text-[#0A0A0A] dark:text-[#F5F5F5]">Run Device Check</span> — posture appears here automatically.</li>
                </ol>
              </div>
              <DialogFooter>
                <Button data-testid="enroll-done-btn" onClick={closeEnroll} className="bg-[#0F62FE] hover:bg-[#0A4FCC] text-white">Done</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
